// import { useState } from 'react'; // TODO: Add interactive interview prep features
import { Navigation } from '../../components/Navigation';
import { MessageSquare, Sparkles, CheckCircle, Clock, Brain, Target, ArrowRight, Play } from 'lucide-react';

export function InterviewFastPass() {

  const features = [
    {
      icon: Clock,
      title: '24/7 Availability',
      description: 'Practice anytime, anywhere with instant AI feedback',
    },
    {
      icon: Brain,
      title: 'Behavioral Question Library',
      description: '500+ real interview questions from top companies',
    },
    {
      icon: Target,
      title: 'Personalized Frameworks',
      description: 'Custom answer structures based on your experience',
    },
    {
      icon: MessageSquare,
      title: 'Real-Time Coaching',
      description: 'Get tone, pace, and content feedback instantly',
    },
  ];

  const questionCategories = [
    {
      name: 'Behavioral',
      count: 150,
      examples: [
        'Tell me about a time you handled conflict',
        'Describe a situation where you showed leadership',
        'How do you handle tight deadlines?',
      ],
    },
    {
      name: 'Technical',
      count: 120,
      examples: [
        'Explain your problem-solving process',
        'Walk me through your workflow',
        'How do you stay organized?',
      ],
    },
    {
      name: 'Situational',
      count: 100,
      examples: [
        'What would you do if a customer was upset?',
        'How would you prioritize multiple tasks?',
        'If your team disagreed, how would you handle it?',
      ],
    },
    {
      name: 'General',
      count: 130,
      examples: [
        'Why do you want this role?',
        'What are your strengths and weaknesses?',
        'Where do you see yourself in 5 years?',
      ],
    },
  ];

  const benefits = [
    'Build confidence through repetition',
    'Learn the STAR method naturally',
    'Get instant feedback on your answers',
    'Practice until you feel ready',
    'Access follow-up email templates',
    'Track your improvement over time',
  ];

  const mockSession = [
    {
      role: 'ai' as const,
      text: "Let's practice! Tell me about a time you overcame a challenge at work.",
    },
    {
      role: 'user' as const,
      text: 'At my last job, we lost a key team member right before a big deadline...',
    },
    {
      role: 'ai' as const,
      text: "Great start! I notice you're setting up the situation well. Let's work on adding more specific metrics about the challenge.",
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <Navigation />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-cyan-100 to-blue-100 px-4 py-2 rounded-full mb-6">
            <Sparkles className="w-5 h-5 text-cyan-600" />
            <span className="text-sm font-semibold text-cyan-900">Interview Coaching Subscription</span>
          </div>

          <h1 className="text-5xl sm:text-6xl font-bold text-gray-900 mb-6">
            Interview FastPass
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            Nail every interview — practice in private, prep in minutes.
          </p>

          <div className="inline-flex items-baseline gap-2 mb-6">
            <span className="text-5xl font-bold text-gray-900">$9.99</span>
            <span className="text-gray-600">/month</span>
          </div>

          <button className="px-10 py-4 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-xl font-bold hover:from-cyan-600 hover:to-blue-600 transition-all shadow-lg hover:shadow-xl text-lg">
            Start Your Free Trial
          </button>

          <p className="text-sm text-gray-500 mt-4">7-day free trial • Cancel anytime</p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {features.map((feature) => {
            const Icon = feature.icon;
            return (
              <div
                key={feature.title}
                className="bg-white rounded-xl p-6 border-2 border-gray-200 hover:border-cyan-300 transition-all hover:shadow-lg"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-xl flex items-center justify-center mb-4">
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-sm text-gray-600">{feature.description}</p>
              </div>
            );
          })}
        </div>

        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Live Practice Session Preview
          </h2>

          <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-lg border-2 border-gray-200 p-8">
            <div className="space-y-4 mb-6">
              {mockSession.map((message, idx) => (
                <div
                  key={idx}
                  className={`flex ${
                    message.role === 'user' ? 'justify-end' : 'justify-start'
                  }`}
                >
                  <div
                    className={`max-w-[80%] rounded-2xl px-5 py-3 ${
                      message.role === 'user'
                        ? 'bg-cyan-500 text-white'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    <p className="text-sm leading-relaxed">{message.text}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-cyan-50 border-2 border-cyan-200 rounded-xl p-6">
              <div className="flex items-start gap-3">
                <Sparkles className="w-6 h-6 text-cyan-600 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">AI Feedback Summary</h4>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <span className="text-sm text-gray-700">Strong situation setup</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <span className="text-sm text-gray-700">Good conversational tone</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Target className="w-5 h-5 text-orange-500" />
                      <span className="text-sm text-gray-700">Add specific metrics and results</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <button className="w-full mt-6 py-3 px-6 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-xl font-bold hover:from-cyan-600 hover:to-blue-600 transition-all flex items-center justify-center gap-2">
              <Play className="w-5 h-5" />
              Try Full Practice Session
            </button>
          </div>
        </div>

        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            500+ Interview Questions
          </h2>

          <div className="grid md:grid-cols-2 gap-6">
            {questionCategories.map((category) => (
              <div
                key={category.name}
                className="bg-white rounded-xl p-6 border-2 border-gray-200 hover:border-cyan-300 transition-all"
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-gray-900">{category.name}</h3>
                  <div className="bg-cyan-100 text-cyan-700 px-3 py-1 rounded-full text-sm font-bold">
                    {category.count} questions
                  </div>
                </div>

                <div className="space-y-2">
                  {category.examples.map((example, idx) => (
                    <div
                      key={idx}
                      className="text-sm text-gray-600 pl-4 border-l-2 border-gray-200 py-1"
                    >
                      {example}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl p-10 text-white mb-16">
          <h2 className="text-3xl font-bold mb-6 text-center">
            What You'll Master
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
            {benefits.map((benefit, idx) => (
              <div key={idx} className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 text-cyan-400 flex-shrink-0 mt-0.5" />
                <span className="text-white/90">{benefit}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-8 mb-16">
          <div className="bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl p-8 text-white">
            <h3 className="text-2xl font-bold mb-4">For Job Seekers</h3>
            <p className="text-white/90 mb-6">
              Build unshakable confidence. Walk into every interview knowing exactly what to say.
            </p>
            <ul className="space-y-3">
              <li className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5" />
                <span>Practice at your own pace</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5" />
                <span>Get better with every session</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5" />
                <span>No judgment, just improvement</span>
              </li>
            </ul>
          </div>

          <div className="bg-gradient-to-br from-orange-500 to-amber-500 rounded-2xl p-8 text-white">
            <h3 className="text-2xl font-bold mb-4">Add to ResuSwipe+</h3>
            <p className="text-white/90 mb-6">
              Already have ResuSwipe+? Add Interview FastPass for just $5.99/month.
            </p>
            <ul className="space-y-3">
              <li className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5" />
                <span>Complete career toolkit</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5" />
                <span>From application to offer</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5" />
                <span>Maximum career momentum</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-10 border-2 border-gray-200 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Try It Risk-Free
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-8">
            Start your 7-day free trial. Practice as much as you want. Cancel anytime if it's not for you.
          </p>
          <button className="px-10 py-4 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-xl font-bold hover:from-cyan-600 hover:to-blue-600 transition-all shadow-lg hover:shadow-xl text-lg inline-flex items-center gap-2">
            Start Free Trial
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}
